import { create } from 'zustand';
import { cabinet } from '../../api/cabinet';
import { useSite } from '../../app/site';
import { defaultPut, uploadDocument, UploadError, validateFile, type PutFn, type UploadApi, type UploadErrorCode, type UploadPhase } from './uploadFlow';

export interface UploadEntry {
  phase: UploadPhase;
  progress: number;
  error: UploadErrorCode | null;
  fileName: string;
  fileSize: number;
  /** Kept in memory only so "try again" does not require picking the file again. */
  file: File;
  caseId: string;
  documentTypeKey?: string;
  /** Present once the file is in storage; a retry then only repeats "complete". */
  storedDocumentId?: string;
  controller?: AbortController;
}

interface UploadsState {
  entries: Record<string, UploadEntry>;
  start: (key: string, args: { caseId: string; file: File; documentTypeKey?: string }) => Promise<boolean>;
  retry: (key: string) => Promise<boolean>;
  cancel: (key: string) => void;
  clear: (key: string) => void;
}

let deps: { api: UploadApi; put: PutFn } = { api: cabinet, put: defaultPut };
/** For tests. */
export function setUploadDeps(d: Partial<typeof deps>) {
  deps = { ...deps, ...d };
}

export const useUploads = create<UploadsState>((set, get) => {
  const patch = (key: string, p: Partial<UploadEntry>) =>
    set((s) => {
      const cur = s.entries[key];
      return cur ? { entries: { ...s.entries, [key]: { ...cur, ...p } } } : s;
    });

  async function run(key: string): Promise<boolean> {
    const e = get().entries[key];
    if (!e) return false;
    const controller = new AbortController();
    patch(key, { controller, error: null, phase: e.storedDocumentId ? 'finishing' : 'preparing' });
    try {
      await uploadDocument({
        api: deps.api,
        put: deps.put,
        caseId: e.caseId,
        file: e.file,
        maxBytes: useSite.getState().maxUploadBytes,
        documentTypeKey: e.documentTypeKey,
        checklistItemId: key,
        signal: controller.signal,
        resumeDocumentId: e.storedDocumentId,
        onPhase: (phase) => patch(key, { phase }),
        onProgress: (progress) => patch(key, { progress }),
        onStored: (id) => patch(key, { storedDocumentId: id }),
      });
      patch(key, { phase: 'done', progress: 1, controller: undefined });
      return true;
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError') {
        get().clear(key);
        return false;
      }
      patch(key, { phase: 'error', error: err instanceof UploadError ? err.code : 'failed', controller: undefined });
      return false;
    }
  }

  return {
    entries: {},
    async start(key, { caseId, file, documentTypeKey }) {
      const invalid = validateFile(file, useSite.getState().maxUploadBytes);
      set((s) => ({
        entries: {
          ...s.entries,
          [key]: { phase: invalid ? 'error' : 'preparing', progress: 0, error: invalid, fileName: file.name, fileSize: file.size, file, caseId, documentTypeKey },
        },
      }));
      if (invalid) return false;
      return run(key);
    },
    retry: (key) => {
      const e = get().entries[key];
      if (!e || e.phase !== 'error' || e.error === 'too_large' || e.error === 'bad_type' || e.error === 'empty') return Promise.resolve(false);
      return run(key);
    },
    cancel(key) {
      get().entries[key]?.controller?.abort();
    },
    clear(key) {
      set((s) => {
        const rest = { ...s.entries };
        delete rest[key];
        return { entries: rest };
      });
    },
  };
});
